/**
 * Enhanced Offers Section Component
 * Displays special offers with validity dates, conditions, and detailed information
 */

import { EnhancedOffer, EnhancedOffersSection } from '../types/informational';
import { InformationalDataService } from '../services/InformationalDataService';

export class EnhancedOffersSectionComponent implements EnhancedOffersSection {
  public title: string;
  public subtitle: string;
  public offers: EnhancedOffer[];
  public showValidityDates: boolean;
  public showConditions: boolean;
  public maxOffers: number;
  public sidebarMode: boolean;

  private container: HTMLElement | null = null;
  private dataService: InformationalDataService;
  private currentSlide: number = 0;
  private autoSlideInterval: NodeJS.Timeout | null = null;

  constructor(
    title: string = 'Limited Special Offers',
    subtitle: string = 'Take advantage of our best offers with validity dates and detailed conditions',
    maxOffers: number = 3,
    showValidityDates: boolean = true,
    showConditions: boolean = true,
    sidebarMode: boolean = false
  ) {
    this.title = title;
    this.subtitle = subtitle;
    this.maxOffers = maxOffers;
    this.showValidityDates = showValidityDates;
    this.showConditions = showConditions;
    this.sidebarMode = sidebarMode;
    this.dataService = InformationalDataService.getInstance();
    this.offers = this.dataService.getEnhancedOffers().slice(0, maxOffers);
  }

  /**
   * Initialize the component
   */
  public init(): void {
    this.createContainer();
    this.render();
    this.bindEvents();
    this.startValidityCheck();
  }

  /**
   * Create the container element
   */
  private createContainer(): void {
    if (this.sidebarMode) {
      // Sidebar mode - inject into hero offers sidebar
      const sidebarContainer = document.getElementById('hero-offers-sidebar');
      if (!sidebarContainer) {
        console.error('EnhancedOffers: Hero offers sidebar not found');
        return;
      }

      // Create container for sidebar
      this.container = document.createElement('div');
      this.container.className = 'hero-offers-compact';
      this.container.id = 'hero-offers-compact';
      this.container.setAttribute('aria-labelledby', 'hero-offers-title');

      // Append to sidebar
      sidebarContainer.appendChild(this.container);
    } else {
      // Normal mode - inject into dynamic sections container
      const dynamicContainer = document.getElementById('dynamic-sections-container');
      if (!dynamicContainer) {
        console.error('EnhancedOffers: Dynamic sections container not found');
        return;
      }

      // Create container
      this.container = document.createElement('section');
      this.container.className = 'enhanced-offers-section';
      this.container.id = 'enhanced-offers-section';
      this.container.setAttribute('aria-labelledby', 'enhanced-offers-title');

      // Append to dynamic container
      dynamicContainer.appendChild(this.container);
    }
  }

  /**
   * Render the component
   */
  private render(): void {
    if (!this.container) return;

    if (this.sidebarMode) {
      // Compact sidebar rendering with carousel
      this.container.innerHTML = `
        <div class="hero-offers-compact__container">
          <header class="hero-offers-compact__header">
            <h3 class="hero-offers-compact__title" id="hero-offers-title">
              🔥 Hot Deals
            </h3>
            <div class="hero-offers-compact__controls">
              <button class="carousel-btn carousel-btn--prev" aria-label="Previous offer">‹</button>
              <div class="carousel-indicators">
                ${this.offers.map((_, index) => `
                  <button class="carousel-indicator ${index === 0 ? 'active' : ''}" data-slide="${index}" aria-label="Go to offer ${index + 1}"></button>
                `).join('')}
              </div>
              <button class="carousel-btn carousel-btn--next" aria-label="Next offer">›</button>
            </div>
          </header>
          <div class="hero-offers-compact__carousel">
            <div class="hero-offers-compact__track" style="transform: translateX(0%)">
              ${this.offers.map(offer => this.renderCompactOfferCard(offer)).join('')}
            </div>
          </div>
        </div>
      `;
    } else {
      // Normal full rendering
      this.container.innerHTML = `
        <div class="enhanced-offers-section__container">
          <header class="enhanced-offers-section__header">
            <h2 class="enhanced-offers-section__title" id="enhanced-offers-title">
              ${this.escapeHtml(this.title)}
            </h2>
            <p class="enhanced-offers-section__subtitle">
              ${this.escapeHtml(this.subtitle)}
            </p>
          </header>

          <div class="enhanced-offers-section__grid" role="region" aria-label="Special offers with conditions">
            ${this.offers.map(offer => this.renderOfferCard(offer)).join('')}
          </div>

          <div class="enhanced-offers-section__disclaimer">
            <p class="enhanced-offers-section__disclaimer-text">
              ⚠️ Demo offers - Prices and conditions are not contractual
            </p>
          </div>
        </div>
      `;
    }
  }

  /**
   * Render a single offer card
   */
  private renderOfferCard(offer: EnhancedOffer): string {
    const discountBadge = offer.price.discount ? `
      <div class="enhanced-offer-card__discount">
        -${offer.price.discount}%
      </div>
    ` : '';

    const originalPrice = offer.price.originalPrice ? `
      <span class="enhanced-offer-card__original-price">${offer.price.originalPrice}€</span>
    ` : '';

    const validityInfo = this.showValidityDates ? `
      <div class="enhanced-offer-card__validity">
        <h4 class="enhanced-offer-card__validity-title">Validité de l'offre</h4>
        <div class="enhanced-offer-card__validity-dates">
          <div class="enhanced-offer-card__validity-item">
            <span class="enhanced-offer-card__validity-label">Valable jusqu'au :</span>
            <span class="enhanced-offer-card__validity-value">${this.formatDate(offer.validity.endDate)}</span>
          </div>
          <div class="enhanced-offer-card__validity-item">
            <span class="enhanced-offer-card__validity-label">Réservation avant le :</span>
            <span class="enhanced-offer-card__validity-value">${this.formatDate(offer.validity.bookingDeadline)}</span>
          </div>
        </div>
        <div class="enhanced-offer-card__countdown" data-end-date="${offer.validity.endDate.toISOString()}">
          ${this.getTimeRemaining(offer.validity.endDate)}
        </div>
      </div>
    ` : '';

    const conditionsInfo = this.showConditions ? `
      <div class="enhanced-offer-card__conditions">
        <h4 class="enhanced-offer-card__conditions-title">Conditions</h4>
        <ul class="enhanced-offer-card__conditions-list">
          ${offer.conditions.map(condition =>
      `<li class="enhanced-offer-card__condition">${this.escapeHtml(condition)}</li>`
    ).join('')}
        </ul>
      </div>
    ` : '';

    const availabilityClass = `enhanced-offer-card__availability--${offer.availability}`;
    const availabilityText = {
      high: 'High availability',
      medium: 'Limited availability',
      low: 'Last seats'
    }[offer.availability];

    const ratingInfo = offer.rating ? `
      <div class="enhanced-offer-card__rating">
        <div class="enhanced-offer-card__stars">
          ${this.renderStars(offer.rating)}
        </div>
        <span class="enhanced-offer-card__rating-text">
          ${offer.rating}/5 (${offer.reviewCount} reviews)
        </span>
      </div>
    ` : '';

    // Generate fallback image name based on offer title
    const fallbackImageName = offer.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

    return `
      <article class="enhanced-offer-card" data-offer-id="${offer.id}">
        <div class="enhanced-offer-card__image">
          <img 
            src="${offer.image}" 
            alt="${this.escapeHtml(offer.title)}"
            class="enhanced-offer-card__img"
            loading="lazy"
            onerror="this.src='/assets/images/offers/${fallbackImageName}.jpg'; if(this.onerror) { this.src='/assets/images/offers/placeholder.jpg'; this.onerror=null; }"
          />
          ${discountBadge}
          <div class="enhanced-offer-card__type">
            ${this.getTypeIcon(offer.type)} ${this.getTypeLabel(offer.type)}
          </div>
        </div>
        
        <div class="enhanced-offer-card__content">
          <div class="enhanced-offer-card__header">
            <h3 class="enhanced-offer-card__title">
              ${this.escapeHtml(offer.title)}
            </h3>
            <p class="enhanced-offer-card__destination">
              📍 ${this.escapeHtml(offer.destination)}
            </p>
          </div>
          
          <p class="enhanced-offer-card__description">
            ${this.escapeHtml(offer.description)}
          </p>
          
          <div class="enhanced-offer-card__highlights">
            <h4 class="enhanced-offer-card__highlights-title">Points forts</h4>
            <ul class="enhanced-offer-card__highlights-list">
              ${offer.highlights.map(highlight =>
      `<li class="enhanced-offer-card__highlight">✓ ${this.escapeHtml(highlight)}</li>`
    ).join('')}
            </ul>
          </div>
          
          <div class="enhanced-offer-card__pricing">
            <div class="enhanced-offer-card__price">
              ${originalPrice}
              <span class="enhanced-offer-card__current-price">${offer.price.amount}€</span>
              <span class="enhanced-offer-card__price-label">par personne</span>
            </div>
            <div class="enhanced-offer-card__availability ${availabilityClass}">
              ${availabilityText}
            </div>
          </div>

          ${ratingInfo}
          ${validityInfo}
          ${conditionsInfo}
          
          <div class="enhanced-offer-card__actions">
            <button 
              class="enhanced-offer-card__cta"
              type="button"
              data-offer-id="${offer.id}"
              aria-label="Book offer ${this.escapeHtml(offer.title)}"
            >
              ${this.escapeHtml(offer.ctaButton.text)}
            </button>
            <button 
              class="enhanced-offer-card__details"
              type="button"
              data-offer-id="${offer.id}"
              aria-label="Voir les détails de l'offre ${this.escapeHtml(offer.title)}"
            >
              Voir les détails
            </button>
          </div>
        </div>
      </article>
    `;
  }

  /**
   * Render compact offer card for sidebar
   */
  private renderCompactOfferCard(offer: EnhancedOffer): string {
    const discountBadge = offer.price.discount > 0 ? `
      <span class="compact-offer__discount">-${offer.price.discount}%</span>
    ` : '';

    const fallbackImageName = offer.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

    return `
      <div class="compact-offer-card" data-offer-id="${offer.id}">
        <div class="compact-offer__image">
          <img 
            src="${offer.image}" 
            alt="${this.escapeHtml(offer.title)}"
            class="compact-offer__img"
            loading="lazy"
            onerror="this.src='/assets/images/offers/${fallbackImageName}.jpg'; if(this.onerror) { this.src='/assets/images/offers/placeholder.jpg'; this.onerror=null; }"
          />
          ${discountBadge}
        </div>
        <div class="compact-offer__content">
          <div class="compact-offer__header">
            <h4 class="compact-offer__title">${this.escapeHtml(offer.title)}</h4>
          </div>
          <p class="compact-offer__destination">${this.escapeHtml(offer.destination)}</p>
          <div class="compact-offer__price">
            <span class="compact-offer__amount">€${offer.price.amount}</span>
            ${offer.price.originalAmount ? `<span class="compact-offer__original">€${offer.price.originalAmount}</span>` : ''}
          </div>
          <button class="compact-offer__cta" data-offer-id="${offer.id}">
            ${this.escapeHtml(offer.ctaButton.text)}
          </button>
        </div>
      </div>
    `;
  }

  /**
   * Get type icon for offer type
   */
  private getTypeIcon(type: string): string {
    const icons = {
      flight: '✈️',
      hotel: '🏨',
      package: '📦',
      train: '🚄'
    };
    return icons[type as keyof typeof icons] || '📦';
  }

  /**
   * Get type label for offer type
   */
  private getTypeLabel(type: string): string {
    const labels = {
      flight: 'Vol',
      hotel: 'Hôtel',
      package: 'Forfait',
      train: 'Train'
    };
    return labels[type as keyof typeof labels] || 'Forfait';
  }

  /**
   * Render star rating
   */
  private renderStars(rating: number): string {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    return '⭐'.repeat(fullStars) +
      (hasHalfStar ? '⭐' : '') +
      '☆'.repeat(emptyStars);
  }

  /**
   * Get time remaining until offer expires
   */
  private getTimeRemaining(endDate: Date): string {
    const now = new Date();
    const timeLeft = endDate.getTime() - now.getTime();

    if (timeLeft <= 0) {
      return 'Offre expirée';
    }

    const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

    if (days > 0) {
      return `⏰ Plus que ${days} jour${days > 1 ? 's' : ''}`;
    } else if (hours > 0) {
      return `⏰ Plus que ${hours}h`;
    } else {
      return '⏰ Dernières heures !';
    }
  }

  /**
   * Start validity check timer
   */
  private startValidityCheck(): void {
    // Update countdown every minute
    setInterval(() => {
      this.updateCountdowns();
    }, 60000);
  }

  /**
   * Update countdown displays
   */
  private updateCountdowns(): void {
    const countdowns = this.container?.querySelectorAll('.enhanced-offer-card__countdown');
    countdowns?.forEach(countdown => {
      const endDateStr = countdown.getAttribute('data-end-date');
      if (endDateStr) {
        const endDate = new Date(endDateStr);
        countdown.textContent = this.getTimeRemaining(endDate);
      }
    });
  }

  /**
   * Bind event listeners
   */
  private bindEvents(): void {
    if (!this.container) return;

    // Handle offer actions
    this.container.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      const ctaButton = target.closest('.enhanced-offer-card__cta, .compact-offer__cta') as HTMLElement;
      const detailsButton = target.closest('.enhanced-offer-card__details') as HTMLElement;
      const prevBtn = target.closest('.carousel-btn--prev') as HTMLElement;
      const nextBtn = target.closest('.carousel-btn--next') as HTMLElement;
      const indicator = target.closest('.carousel-indicator') as HTMLElement;

      if (ctaButton) {
        const offerId = ctaButton.getAttribute('data-offer-id');
        if (offerId) {
          this.handleOfferBooking(offerId);
        }
      } else if (detailsButton) {
        const offerId = detailsButton.getAttribute('data-offer-id');
        if (offerId) {
          this.handleOfferDetails(offerId);
        }
      } else if (prevBtn && this.sidebarMode) {
        this.previousSlide();
      } else if (nextBtn && this.sidebarMode) {
        this.nextSlide();
      } else if (indicator && this.sidebarMode) {
        const slideIndex = parseInt(indicator.getAttribute('data-slide') || '0');
        this.goToSlide(slideIndex);
      }
    });

    // Initialize carousel if in sidebar mode
    if (this.sidebarMode) {
      this.initializeCarousel();
      
      // Pause auto-slide on hover
      this.container.addEventListener('mouseenter', () => {
        this.stopAutoSlide();
      });
      
      // Resume auto-slide when mouse leaves
      this.container.addEventListener('mouseleave', () => {
        this.startAutoSlide();
      });
    }

    // Handle keyboard navigation
    this.container.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        const target = event.target as HTMLElement;
        if (target.classList.contains('enhanced-offer-card__cta') ||
          target.classList.contains('enhanced-offer-card__details')) {
          event.preventDefault();
          target.click();
        }
      }
    });
  }

  /**
   * Handle offer booking
   */
  private handleOfferBooking(offerId: string): void {
    const offer = this.offers.find(o => o.id === offerId);
    if (!offer) return;

    // Check if offer is still valid
    const now = new Date();
    if (now > offer.validity.endDate) {
      alert('This offer has expired. Please check our other available offers.');
      return;
    }

    if (now > offer.validity.bookingDeadline) {
      alert('La date limite de réservation pour cette offre est dépassée.');
      return;
    }

    console.log(`Booking offer: ${offer.title} - ${offer.price.amount}€`);
    offer.ctaButton.onClick();
  }

  /**
   * Handle offer details
   */
  private handleOfferDetails(offerId: string): void {
    const offer = this.offers.find(o => o.id === offerId);
    if (!offer) return;

    console.log(`Viewing details for offer: ${offer.title}`);
    // In a real application, this would open a modal or navigate to details page
  }

  /**
   * Format date for display
   */
  private formatDate(date: Date): string {
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  }

  /**
   * Update offers data
   */
  public updateOffers(offers: EnhancedOffer[]): void {
    this.offers = offers.slice(0, this.maxOffers);
    this.render();
  }

  /**
   * Escape HTML to prevent XSS
   */
  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Initialize carousel functionality
   */
  private initializeCarousel(): void {
    this.currentSlide = 0;
    this.startAutoSlide();
  }

  /**
   * Go to specific slide
   */
  private goToSlide(index: number): void {
    if (!this.container) return;
    
    this.currentSlide = index;
    const track = this.container.querySelector('.hero-offers-compact__track') as HTMLElement;
    const indicators = this.container.querySelectorAll('.carousel-indicator');
    
    if (track) {
      const translateX = -index * 100;
      track.style.transform = `translateX(${translateX}%)`;
    }
    
    // Update indicators
    indicators.forEach((indicator, i) => {
      indicator.classList.toggle('active', i === index);
    });
  }

  /**
   * Go to next slide
   */
  private nextSlide(): void {
    const nextIndex = (this.currentSlide + 1) % this.offers.length;
    this.goToSlide(nextIndex);
  }

  /**
   * Go to previous slide
   */
  private previousSlide(): void {
    const prevIndex = this.currentSlide === 0 ? this.offers.length - 1 : this.currentSlide - 1;
    this.goToSlide(prevIndex);
  }

  /**
   * Start automatic slide rotation
   */
  private startAutoSlide(): void {
    if (this.autoSlideInterval) {
      clearInterval(this.autoSlideInterval);
    }
    
    this.autoSlideInterval = setInterval(() => {
      this.nextSlide();
    }, 4000); // Change slide every 4 seconds
  }

  /**
   * Stop automatic slide rotation
   */
  private stopAutoSlide(): void {
    if (this.autoSlideInterval) {
      clearInterval(this.autoSlideInterval);
      this.autoSlideInterval = null;
    }
  }

  /**
   * Destroy the component
   */
  public destroy(): void {
    this.stopAutoSlide();
    if (this.container) {
      this.container.remove();
      this.container = null;
    }
  }
}